import { ModalRoute } from "../../Navigation/ModalRoute";
import { ModalTransitionSwitch } from "../../Navigation/ModalTransitionSwitch";
import {
  DOCUMENT_CAMERA_PERMISSION_ERROR_PATH,
  DOCUMENT_CHECKR_ERROR_PATH,
  DOCUMENT_CHECKR_LOADING_PATH,
  DOCUMENT_DELETE_CONFIRMATION_PATH,
  DOCUMENT_REQUIRED_BY_WORKPLACES_PATH,
  DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_PATH,
  DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH,
  DOCUMENT_STRIPE_VERIFICATION_SELECT_SUBTYPE_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_PROGRESS_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
  DOCUMENT_SUBMISSION_REJECTED_PATH,
  DOCUMENT_TB_TEST_GUIDED_UPLOAD_PATH,
  DOCUMENT_UPLOAD_MODAL_PATH,
} from "../paths";
import { DocumentSubmissionApprovedBottomSheet } from "../Submission/ApprovedBottomSheet";
import { DocumentCameraPermissionErrorBottomSheet } from "../Submission/CameraPermissionErrorBottomSheet";
import { DocumentSubmissionErrorBottomSheet } from "../Submission/ErrorBottomSheet";
import { DocumentSubmissionInProgressBottomSheet } from "../Submission/InProgressBottomSheet";
import { DocumentSubmissionInReviewBottomSheet } from "../Submission/InReviewBottomSheet";
import { DocumentSubmissionRejectedBottomSheet } from "../Submission/RejectedBottomSheet";
import { DocumentDeleteConfirmationBottomSheet } from "./DeleteConfirmationBottomSheet";
import { DocumentDetailsRequiredByWorkplacesBottomSheet } from "./RequiredBySection/BottomSheet";
import { DocumentDetailsUploadBottomSheet } from "./Upload/BottomSheet";
import { CheckrErrorBottomSheet } from "./Workflows/Checkr/ErrorBottomSheet";
import { CheckrLoadingBottomSheet } from "./Workflows/Checkr/LoadingBottomSheet";
import { DocumentDetailsStripeIdentityVerificationInProgressBottomSheet } from "./Workflows/Stripe/InProgressBottomSheet";
import { DocumentDetailsStripeIdentityVerificationRejectedBottomSheet } from "./Workflows/Stripe/RejectedBottomSheet";
import { DocumentDetailsStripeVerificationSelectSubtypeBottomSheet } from "./Workflows/Stripe/SelectSubtypeBottomSheet";
import { TbTestGuidedUploadDialog } from "./Workflows/TbTestGuidedUpload/components/TbTestGuidedUploadDialog";

interface DocumentDetailsModalRoutesProps {
  basePath: string;
}
/**
 * DocumentDetailsModalRoutes handles all document details-related modals
 *
 * They render on top of the document details page or document upload preview page

 * Common flows:
 * - Document upload modal: /documents/:requirementId/upload
 * - Document delete confirmation: /documents/:requirementId/delete-confirmation/:documentId
 * - Document submission states:
 *   - In progress: /documents/:requirementId/submission-in-progress
 *   - Error: /documents/:requirementId/submission-error
 *   - In review: /documents/:requirementId/submission-in-review
 *   - Approved: /documents/:requirementId/submission-approved
 */
export function DocumentDetailsModalRoutes(props: DocumentDetailsModalRoutesProps) {
  const { basePath } = props;

  return (
    <ModalTransitionSwitch>
      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_UPLOAD_MODAL_PATH}`}
        render={({ modalState }) => {
          return <DocumentDetailsUploadBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_SUBMISSION_IN_PROGRESS_PATH}`}
        render={({ modalState }) => {
          return <DocumentSubmissionInProgressBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_SUBMISSION_ERROR_PATH}`}
        render={({ modalState }) => {
          return <DocumentSubmissionErrorBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_SUBMISSION_IN_REVIEW_PATH}`}
        render={({ modalState }) => {
          return <DocumentSubmissionInReviewBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_SUBMISSION_APPROVED_PATH}`}
        render={({ modalState }) => {
          return <DocumentSubmissionApprovedBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_SUBMISSION_REJECTED_PATH}`}
        render={({ modalState }) => {
          return <DocumentSubmissionRejectedBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_CHECKR_LOADING_PATH}`}
        render={({ modalState }) => {
          return <CheckrLoadingBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_CHECKR_ERROR_PATH}`}
        render={({ modalState }) => {
          return <CheckrErrorBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_CAMERA_PERMISSION_ERROR_PATH}`}
        render={({ modalState }) => {
          return <DocumentCameraPermissionErrorBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_REQUIRED_BY_WORKPLACES_PATH}`}
        render={({ modalState }) => {
          return <DocumentDetailsRequiredByWorkplacesBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_DELETE_CONFIRMATION_PATH}`}
        render={({ modalState }) => {
          return <DocumentDeleteConfirmationBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_TB_TEST_GUIDED_UPLOAD_PATH}`}
        render={({ modalState }) => {
          return <TbTestGuidedUploadDialog modalState={modalState} />;
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_STRIPE_VERIFICATION_SELECT_SUBTYPE_PATH}`}
        render={({ modalState }) => {
          return (
            <DocumentDetailsStripeVerificationSelectSubtypeBottomSheet modalState={modalState} />
          );
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_PATH}`}
        render={({ modalState }) => {
          return (
            <DocumentDetailsStripeIdentityVerificationInProgressBottomSheet
              modalState={modalState}
            />
          );
        }}
      />

      <ModalRoute
        shouldReplacePathOnBack
        closeModalPath={basePath}
        path={`${basePath}/${DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH}`}
        render={({ modalState }) => {
          return (
            <DocumentDetailsStripeIdentityVerificationRejectedBottomSheet modalState={modalState} />
          );
        }}
      />
    </ModalTransitionSwitch>
  );
}
